/**
 * „Nummern ohne Bild" — die Herstellernummern aller anlegbaren Artikel, für die ein Bild
 * fehlt, als Liste zum Hochladen im Herstellerportal (adidas: Click-Portal, SOP
 * 15.10.2026). Eine Nummer je Zeile; kopieren oder als .txt speichern.
 *
 * Live gegen den Bildindex: sind die Bilder auf dem FTP und eingelesen, fallen ihre
 * Nummern beim nächsten Klick aus der Liste. Pfade OHNE `/api/`-Prefix.
 */
import { useState } from 'react';
import { ClipboardCopy, Download, ImageOff, Loader2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import Info from './Info';
import { zahl } from '../lib/format';

interface Antwort {
  nummern: string[];
  artikel: Array<{ artikelSchluessel: string; name: string; nummern: string[] }>;
  geprueft: boolean;
  stand: string | null;
}

export default function FehlendeBilder({ entwurfId, entwurfName }: { entwurfId: number; entwurfName: string }) {
  const [antwort, setAntwort] = useState<Antwort | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null);

  const laden = async () => {
    setLaedt(true);
    setMeldung(null);
    try {
      const r = await api.get<{ data: Antwort }>(`/programmes/artikelimport/entwuerfe/${entwurfId}/fehlende-bilder`);
      setAntwort(r.data.data);
    } catch (e: any) {
      setMeldung({ text: e?.response?.data?.error?.message ?? 'Die Liste ließ sich nicht laden.', fehler: true });
    } finally {
      setLaedt(false);
    }
  };

  const text = antwort ? antwort.nummern.join('\n') : '';

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setMeldung({ text: `${zahl(antwort?.nummern.length ?? 0)} Nummern kopiert.`, fehler: false });
    } catch {
      setMeldung({ text: 'Kopieren ging nicht — bitte aus dem Feld markieren.', fehler: true });
    }
  };

  const speichern = () => {
    const datei = new Blob([`${text}\n`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(datei);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nummern-ohne-bild-${entwurfName.replace(/[^\w-]+/g, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-2 rounded border border-[#DAD9D9] bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
          <ImageOff className="h-4 w-4" aria-hidden /> Nummern ohne Bild
          <Info titel="Wofür ist die Liste?">
            <p>Alle Herstellernummern (bei adidas die Farbnummer, z.&nbsp;B. JD9099), für die ein neuer Artikel noch kein Bild hat — eine Nummer je Zeile, zum Hochladen im Herstellerportal (adidas: Click-Portal).</p>
            <p>Artikel, die schon in Plenty stehen, fehlen in der Liste. Liegen die Bilder danach auf dem FTP, fallen ihre Nummern beim nächsten Klick heraus.</p>
          </Info>
        </h3>
        <Button
          size="sm"
          variant="outline"
          disabled={laedt}
          onClick={() => void laden()}
        >
          {laedt ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> : null}
          {antwort ? 'Neu prüfen' : 'Liste erstellen'}
        </Button>
      </div>

      {antwort && !antwort.geprueft && (
        <p className="text-xs text-amber-800">Der Bildindex ist nicht lesbar — es lässt sich nicht sagen, welche Bilder fehlen.</p>
      )}

      {antwort?.geprueft && antwort.nummern.length === 0 && (
        <p className="text-xs text-green-800">Für alle anlegbaren Artikel liegen Bilder vor.</p>
      )}

      {antwort?.geprueft && antwort.nummern.length > 0 && (
        <>
          <p className="text-xs text-[#1A1A1A]/70">
            {zahl(antwort.nummern.length)} Nummern aus {zahl(antwort.artikel.length)} Artikeln
          </p>
          <textarea
            readOnly
            value={text}
            rows={Math.min(10, antwort.nummern.length)}
            className="w-full max-w-xs rounded border border-[#DAD9D9] px-2 py-1 font-mono text-xs"
            onFocus={e => e.currentTarget.select()}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => void kopieren()}>
              <ClipboardCopy className="mr-1 h-4 w-4" aria-hidden /> Kopieren
            </Button>
            <Button size="sm" variant="outline" onClick={speichern}>
              <Download className="mr-1 h-4 w-4" aria-hidden /> Als .txt speichern
            </Button>
          </div>
          <details className="text-xs text-[#1A1A1A]/80">
            <summary className="cursor-pointer">Je Artikel</summary>
            <ul className="mt-1 space-y-0.5">
              {antwort.artikel.map(a => (
                <li key={a.artikelSchluessel}>
                  <span className="font-medium">{a.name}</span>: <span className="font-mono">{a.nummern.join(', ')}</span>
                </li>
              ))}
            </ul>
          </details>
        </>
      )}

      {meldung && <p className={`text-xs ${meldung.fehler ? 'text-red-800' : 'text-green-800'}`}>{meldung.text}</p>}
    </div>
  );
}
